import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AntennaIcon from "../../assets/svg/Antenna.svg";
import CubeIcon from "../../assets/svg/Cube.svg";
import { CompanyContext } from "../../contexts/Company";
import { UserContext } from "../../contexts/User";

const SIM_CARD_TYPES = [
  { type: "2FF", label: "Comum 2FF" },
  { type: "triple", label: "Triplo Corte" },
];

const useStyles = makeStyles({
  cardContainer: {
    display: "block",
    backgroundColor: "#F8F8F8",
    padding: "0px 15px",
    borderRadius: "15px",
    height: "380px",
    marginTop: 24,
    width: "290px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
    flexShrink: 0,
  },
  cardDetails: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    height: "100%",
  },
  logoHeader: {
    textAlign: "center",
    margin: "0 auto",
    width: "100%",
  },
  images: {
    width: "100px",
    height: "100px",
    objectFit: "contain",
    userSelect: "none",
    padding: 0,
    margin: 0,
  },
  centerContent: {
    width: "100%",
    padding: "5px 0px 20px 0px",
  },
  tagsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  selectorContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  cutTypesContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    gap: 10,
  },
  buyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: 20,
    gap: 8,
  },
  price: {
    fontWeight: 600,
    fontSize: "1.05em",
    color: "#050821",
    margin: 0,
  },
});

const formatPrice = (price) => {
  if (price == null || Number.isNaN(Number(price))) return "R$ 0,00";
  return (Number(price) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export const ProductCard = ({
  carrier,
  availablePlans = [],
  isMulti = false,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [user] = useContext(UserContext);
  const [company] = useContext(CompanyContext);

  const [cutType, setCutType] = useState(SIM_CARD_TYPES[0].type);
  const [selectedPlan, setSelectedPlan] = useState(availablePlans[0] || null);

  useEffect(() => {
    if (!availablePlans?.length) {
      setSelectedPlan(null);
      return;
    }

    const stillAvailable = availablePlans.find(
      (plan) => plan.id === selectedPlan?.id
    );
    if (!stillAvailable) {
      setSelectedPlan(availablePlans[0]);
    }
  }, [availablePlans, selectedPlan?.id]);

  const handleChangePlan = (id) => {
    const plan = availablePlans.find((item) => item.id === id);
    if (plan) setSelectedPlan(plan);
  };

  const handleOpenProduct = () => {
    if (!selectedPlan) return;

    if (user.systemRole === 4 && company.company_id === undefined) {
      enqueueSnackbar("Selecione uma empresa antes de acessar o produto", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      return;
    }

    navigate(`/product/${selectedPlan.id}`, {
      state: { chipType: cutType },
    });
  };

  if (!availablePlans?.length || !selectedPlan) return null;

  const imageSrc = `${
    process.env.REACT_APP_API_URL
  }/public/images/product/${carrier?.toLowerCase()}`;

  return (
    <div className={classes.cardContainer}>
      <div className={classes.cardDetails}>
        <Box className={classes.logoHeader}>
          <img className={classes.images} src={imageSrc} alt={carrier} />
        </Box>

        <Box className={classes.centerContent}>
          <Box className={classes.tagsContainer}>
            <Chip
              sx={{ backgroundColor: "#050821", color: "white" }}
              label={
                <Box sx={{ display: "flex", flexDirection: "row" }} gap={1}>
                  <img src={CubeIcon} alt="" height={20} />
                  Em estoque
                </Box>
              }
              size="small"
            />
            {isMulti ? (
              <Chip
                sx={{ backgroundColor: "error.main", color: "white" }}
                label={
                  <Box sx={{ display: "flex", flexDirection: "row" }} gap={1}>
                    <img src={AntennaIcon} alt="" height={20} />
                    Multioperadora
                  </Box>
                }
                size="small"
              />
            ) : null}
          </Box>

          <Box className={classes.selectorContainer}>
            <FormControl
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": { borderColor: "#C9C9C9" },
                  "&:hover fieldset": { borderColor: "#9E9E9E" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#050821",
                    borderWidth: 1,
                  },
                },
                "& .MuiSelect-select": {
                  paddingRight: "40px !important",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#050821" },
                "& .MuiSelect-icon": {
                  color: "#050821 !important",
                  height: "24px !important",
                  width: "24px !important",
                  marginRight: "0 !important",
                  right: "10px !important",
                  top: "calc(50% - 12px) !important",
                  position: "absolute !important",
                },
              }}
            >
              <InputLabel id={`plans-label-${carrier}`}>Planos</InputLabel>
              <Select
                labelId={`plans-label-${carrier}`}
                value={selectedPlan.id}
                label="Planos"
                onChange={(e) => handleChangePlan(e.target.value)}
                fullWidth
              >
                {availablePlans.map(({ id, name }) => (
                  <MenuItem key={`plan-${id}`} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={classes.cutTypesContainer}>
            <Button
              size="small"
              onClick={() => setCutType(SIM_CARD_TYPES[0].type)}
              variant={
                cutType === SIM_CARD_TYPES[0].type ? "contained" : "outlined"
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                ...(cutType === SIM_CARD_TYPES[0].type
                  ? {
                      backgroundColor: "#050821 !important",
                      color: "#fff !important",
                      border: "1px solid #050821",
                    }
                  : {
                      color: "#050821 !important",
                      border: "1px solid #050821 !important",
                      backgroundColor: "#fff !important",
                    }),
              }}
            >
              {SIM_CARD_TYPES[0].label}
            </Button>
            <Button
              size="small"
              onClick={() => setCutType(SIM_CARD_TYPES[1].type)}
              variant={
                cutType === SIM_CARD_TYPES[1].type ? "contained" : "outlined"
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                ...(cutType === SIM_CARD_TYPES[1].type
                  ? {
                      backgroundColor: "#050821 !important",
                      color: "#fff !important",
                      border: "1px solid #050821",
                    }
                  : {
                      color: "#050821 !important",
                      border: "1px solid #050821 !important",
                      backgroundColor: "#fff !important",
                    }),
              }}
            >
              {SIM_CARD_TYPES[1].label}
            </Button>
          </Box>

          <Box className={classes.buyContainer}>
            <p className={classes.price}>
              {formatPrice(selectedPlan.price_unit)}
            </p>
            <Button
              size="large"
              variant="contained"
              color="success"
              fullWidth
              onClick={handleOpenProduct}
              sx={{ color: "white", fontSize: 18, maxWidth: 170 }}
            >
              Comprar
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
};
